import { ReactElement, ReactNode } from 'react'

import AdjustmentsIcon from '@heroicons/react/outline/esm/AdjustmentsIcon.js'
import CogIcon from '@heroicons/react/outline/esm/CogIcon.js'
import LogoutIcon from '@heroicons/react/outline/esm/LogoutIcon.js'
import ShieldCheckIcon from '@heroicons/react/outline/esm/ShieldCheckIcon.js'
import SupportIcon from '@heroicons/react/outline/esm/SupportIcon.js'
import UserCircleIcon from '@heroicons/react/outline/esm/UserCircleIcon.js'
import { clsx } from 'clsx'
import { Link } from 'react-router-dom'

import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import { useFirstRenderContext } from '@tribeplatform/react-components/common/ssr'
import { useI18n } from '@tribeplatform/react-components/i18n'
import {
  SystemSpaceSlug,
  useAuthToken,
  useSpaceUtil,
} from '@tribeplatform/react-sdk/hooks'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { IndicatorBadge } from '@tribeplatform/react-ui-kit/IndicatorBadge'

import { useHelp } from '../../../hooks/useHelp.js'
import { useLogout } from '../../../hooks/useLogout.js'
import { useModerationCount } from '../../../hooks/useModerationCount.js'
import { NavbarControlItem } from '../constants.js'
import { ModerationIndicatorBadge } from '../ModerationIndicatorBadge.js'

type ControlProfileItem = {
  name?: string
  href?: string
  key?: string
  icon?: ReactNode
  indicator?: number
  as?: 'a' | typeof Link
  action?: () => void
}

export type NavbarControlProfileProps = {
  item: NavbarControlItem
  presentation: ReactElement
}

export const NavbarControlProfile = ({
  item,
  presentation,
}: NavbarControlProfileProps): ReactElement => {
  const { $t } = useI18n()
  const {
    data: { network },
  } = useAuthToken()

  const { logout } = useLogout()
  const { openHelpAndSupport } = useHelp()
  const { generatePath, generateProfilePath } = useSpaceUtil()
  const { total } = useModerationCount()

  const [canUpdateNetwork] = hasScopesPermission(network, ['updateNetwork'])
  const [canGetModerations] = hasScopesPermission(network, ['getModerations'])

  const accountSettingsPath = generatePath(SystemSpaceSlug.PROFILE_SETTINGS)

  let profileItems: Array<ControlProfileItem> = [
    {
      name: $t({
        defaultMessage: 'Your profile',
        id: 'Navbar.Controls.YourProfile',
      }),
      href: generateProfilePath(),
      icon: <UserCircleIcon />,
      key: 'your-profile',
    },
    {
      name: $t({
        defaultMessage: 'Account settings',
        id: 'Navbar.Controls.AccountSettings',
      }),
      href: `${accountSettingsPath}/account`,
      icon: <AdjustmentsIcon />,
      key: 'account-settings',
    },
  ]
  if (canUpdateNetwork) {
    profileItems.push({ name: '-' })
    profileItems.push({
      name: $t({
        defaultMessage: 'Administration',
        id: 'Generics.Administration',
      }),
      href: '/manage/people/members',
      icon: <CogIcon />,
      key: 'administration',
    })
  }
  if (canGetModerations) {
    profileItems.push({
      name: $t({
        defaultMessage: 'Moderation',
        id: 'Generics.Moderation',
      }),
      href: '/settings/moderation',
      icon: <ShieldCheckIcon />,
      key: 'customizer',
      indicator: total,
    })
  }
  if (canUpdateNetwork) {
    profileItems = profileItems.concat([
      {
        name: $t({
          defaultMessage: 'Help & community',
          id: 'RESERVED.Support',
        }),
        action: () => {
          openHelpAndSupport()
        },
        icon: <SupportIcon />,
        key: 'help',
      },
    ])
  }
  profileItems = profileItems.concat([
    { name: '-' },
    {
      name: $t({
        defaultMessage: 'Sign out',
        id: 'Generics.SignOut',
      }),
      action: () => logout(),
      icon: <LogoutIcon />,
      key: 'sign-out',
    },
  ])

  /**
   * @tag Vite migration
   * Dropdown produces hydration error because of prop `id` did not match.
   * As a workaround, Dropdown is initialized client-side only
   */
  const { isFirstRender } = useFirstRenderContext()

  if (isFirstRender) {
    return presentation
  }

  return (
    <ModerationIndicatorBadge className={clsx('translate-y-0')}>
      <Dropdown placement="bottom-end">
        <Dropdown.ButtonMinimal
          className="navbar-profile-menu"
          aria-label={item.label}
        >
          {presentation}
        </Dropdown.ButtonMinimal>
        <Dropdown.Items className="navbar-profile-menu__dropdown">
          {profileItems.map((item, index) => {
            if (!item) {
              return null
            }
            if (item?.name === '-') {
              return (
                <Dropdown.Divider
                  // eslint-disable-next-line react/no-array-index-key
                  key={`static-key-navbar-divider-${index}`}
                  className="menu-divider"
                />
              )
            }

            if (item.as === 'a') {
              return (
                <Dropdown.Item
                  leadingIcon={item.icon as any}
                  key={item.name}
                  as="a"
                  href={item.href || '#'}
                  onClick={item.action}
                  data-id={item.key}
                >
                  {item.name}
                </Dropdown.Item>
              )
            }

            if (item.href) {
              return (
                <Dropdown.Item
                  leadingIcon={item.icon as any}
                  key={item.name}
                  as={Link}
                  to={item.href}
                  onClick={item.action}
                  data-id={item.key}
                >
                  <div className="flex justify-between">
                    {item.name}
                    {!!item.indicator && (
                      <IndicatorBadge count={item.indicator} />
                    )}
                  </div>
                </Dropdown.Item>
              )
            }

            return (
              <Dropdown.Item
                leadingIcon={item.icon as any}
                key={item.name}
                onClick={item.action}
                data-id={item.key}
              >
                {item.name}
              </Dropdown.Item>
            )
          })}
        </Dropdown.Items>
      </Dropdown>
    </ModerationIndicatorBadge>
  )
}
